import React, { createContext, FC, ReactNode, useContext, useMemo } from 'react';
import flagsmith from 'flagsmith';
import { FlagsmithProvider, useFlags } from 'flagsmith/react';
import { FeatureFlags, IFeatureFlagsResponse } from 'types';

const FLAGSMITH_API_KEY = process.env.REACT_APP_FLAGSMITH_API_KEY;

interface IProps {
  children: ReactNode;
}

interface IContentProps {
  children: ReactNode;
}

interface IFeatureFlags {
  getFeatureFlags(names: FeatureFlags[]): IFeatureFlagsResponse;
}

const FeatureFlagsContext = createContext<IFeatureFlags | null>(null);

export const useFeatureFlags = (names: FeatureFlags[]) => useContext(FeatureFlagsContext)?.getFeatureFlags(names) || {};

const CustomFeatureFlagsProvider: FC<IContentProps> = ({ children }) => {
  const allFlags = useFlags(Object.values(FeatureFlags));

  const getFeatureFlags = (names: FeatureFlags[]) => {
    const flags: IFeatureFlagsResponse = {};

    for (let i = 0; i < names.length; i++) {
      const flag = allFlags[names[i]];
      // eslint-disable-next-line no-continue
      if (!flag) continue;
      flags[names[i]] = {
        enabled: flag.enabled,
      };
    }

    return flags;
  };

  const value: IFeatureFlags = useMemo(
    () => ({
      getFeatureFlags,
    }),
    [getFeatureFlags]
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

export const FeatureFlagsProvider: FC<IProps> = ({ children }) => (
  <FlagsmithProvider flagsmith={flagsmith} options={{ environmentID: FLAGSMITH_API_KEY!, cacheFlags: true }}>
    <CustomFeatureFlagsProvider>{children}</CustomFeatureFlagsProvider>
  </FlagsmithProvider>
);
